import { Badge, Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import { useNavigate } from 'react-router-dom'
import { useCreateFollowUpList } from '../../hooks/useLobbyDay'
import type { RosterEntry } from './debriefRoster'
import type { AskResponse } from '../../types/lobbyDay'

interface Props {
  eventId: string
  askId: string | null
  askTitle: string | null
  response: AskResponse
  roster: RosterEntry[]
}

export function FollowUpRosterReveal({ eventId, askId, response, roster }: Props) {
  const navigate = useNavigate()
  const createList = useCreateFollowUpList(eventId)

  const onCreate = async () => {
    if (!askId) return
    try {
      const res = await createList.mutateAsync({ askId, response })
      toaster.create({ type: 'success', title: res.label, description: `${res.memberCount} contacts` })
      navigate(`/dashboard/lists/${res.listId}/edit`)
    } catch {
      toaster.create({ type: 'info', title: 'No contacts with this outcome yet' })
    }
  }

  return (
    <Box borderWidth="1px" borderRadius="md" p={3} mt={2} bg="gray.50">
      <VStack align="stretch" gap={1} mb={3}>
        {roster.length === 0 && <Text fontSize="sm" color="gray.500">No attendees for this outcome yet.</Text>}
        {roster.length > 0 && (
          <Text fontSize="xs" color="gray.500">
            Preview of attendees — the final list is built from outcome tags and may differ.
          </Text>
        )}
        {roster.map((r) => (
          <HStack key={r.contactId} justify="space-between">
            <Text fontSize="sm" fontWeight="medium">{r.fullName}</Text>
            <HStack gap={2} color="gray.500" fontSize="xs">
              {r.district && <Badge>{r.district}</Badge>}
              <Text lineClamp={1}>{r.legislatorName}{r.askTitle ? ` · ${r.askTitle}` : ''}</Text>
            </HStack>
          </HStack>
        ))}
      </VStack>
      <Button
        size="sm"
        colorPalette="brand"
        minH={{ base: '44px', md: 'auto' }}
        disabled={!askId || roster.length === 0}
        loading={createList.isPending}
        onClick={onCreate}
      >
        Create follow-up list
      </Button>
    </Box>
  );
}
